import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/ui/button';
import { IconChat, IconSettings, IconTasks } from '@/components/ui/icons';
import { Empty, ErrorBox } from '@/components/ui/misc';
import { call } from '@/lib/api';
import {
  type ActivityFilter,
  type CompanyActivityItem,
  companyActivity,
  filterCompanyActivity,
} from '@/lib/company-activity';
import { relTime } from '@/lib/format';
import { useCan } from '@/lib/permissions';
import { qk, useAgents } from '@/lib/queries';
import { friendlyTool } from '@/lib/tool-display';
import { cn } from '@/lib/utils';

const FILTERS = ['all', 'messages', 'tasks', 'system'] as const;
const ICON = { messages: IconChat, tasks: IconTasks, system: IconSettings };

/** Read-only, bounded view of existing channel posts and run evidence. */
export function CompanyActivity() {
  const agents = useAgents();
  const canReadRuns = useCan('run:read');
  const canReadTasks = useCan('task:read');
  const canReadChannels = useCan('conversation:read');
  const channels = useQuery({
    queryKey: qk.channels,
    queryFn: () => call('listChannels'),
    enabled: canReadChannels,
  });
  const tasks = useQuery({
    queryKey: qk.tasks,
    queryFn: () => call('listTasks', { query: { limit: 200 } }),
    enabled: canReadTasks,
  });
  const runs = useQuery({
    queryKey: qk.runs({ scope: 'company-activity' }),
    queryFn: () => call('listRuns', { query: { limit: 8 } }),
    enabled: canReadRuns,
  });
  const recentChannels = [...(canReadChannels ? (channels.data ?? []) : [])]
    .sort((a, b) => (b.lastPostAt ?? b.createdAt).localeCompare(a.lastPostAt ?? a.createdAt))
    .slice(0, 8);
  const postQueries = useQueries({
    queries: recentChannels.map((c) => ({
      queryKey: ['channels', c.id, 'activity'],
      queryFn: () => call('listChannelPosts', { params: { id: c.id }, query: { limit: 50 } }),
      staleTime: 15_000,
    })),
  });
  const eventQueries = useQueries({
    queries: (canReadRuns ? (runs.data ?? []) : []).map((r) => ({
      queryKey: ['runs', 'company-activity-events', r.id],
      queryFn: () => call('runEvents', { params: { id: r.id }, query: { after: 0, limit: 1000 } }),
      staleTime: 15_000,
      refetchInterval: r.endedAt ? (false as const) : 15_000,
    })),
  });
  const queries = [
    agents,
    ...(canReadRuns ? [runs] : []),
    ...(canReadTasks ? [tasks] : []),
    ...(canReadChannels ? [channels] : []),
    ...postQueries,
    ...eventQueries,
  ];
  const items = companyActivity({
    agents: agents.data ?? [],
    channels: canReadChannels ? (channels.data ?? []) : [],
    tasks: canReadTasks ? (tasks.data ?? []) : [],
    posts: postQueries.flatMap((q) => q.data ?? []),
    runs: (canReadRuns ? (runs.data ?? []) : []).map((run, i) => ({
      run,
      events: eventQueries[i]?.data ?? [],
    })),
  });
  return (
    <CompanyActivityList
      items={items}
      loading={queries.some((q) => q.isLoading)}
      error={queries.find((q) => q.error)?.error}
    />
  );
}

export function CompanyActivityList({
  items,
  loading = false,
  error,
}: {
  items: CompanyActivityItem[];
  loading?: boolean;
  error?: unknown;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const visible = filterCompanyActivity(items, filter);
  return (
    <section aria-label={t('companyActivity.title')} className="min-w-0 flex-1 overflow-y-auto p-3 sm:p-4">
      <h2 className="text-base font-semibold">{t('companyActivity.title')}</h2>
      <p className="mt-1 text-sm text-muted">{t('companyActivity.legend')}</p>
      <fieldset className="my-3 flex flex-wrap gap-2">
        <legend className="sr-only">{t('companyActivity.filter')}</legend>
        {FILTERS.map((value) => {
          const Icon = value === 'all' ? null : ICON[value];
          return (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-accent',
                filter === value ? 'bg-panel-2 font-medium' : 'text-muted hover:bg-hover',
              )}
            >
              {Icon ? <Icon size={14} /> : null}
              {t(`companyActivity.filters.${value}`)}
            </button>
          );
        })}
      </fieldset>
      <p className="mb-3 text-xs text-muted">{t('companyActivity.window')}</p>
      {error ? (
        <>
          <ErrorBox error={error} />
          <p role="status" className="my-2 text-sm text-muted">
            {t('companyActivity.partial')}
          </p>
        </>
      ) : null}
      {loading ? <Spinner /> : null}
      {visible.length ? (
        <ul aria-label={t('companyActivity.title')} className="flex flex-col gap-3">
          {visible.map((item) => (
            <ActivityRow key={item.id} item={item} />
          ))}
        </ul>
      ) : !loading ? (
        <Empty>
          {t(error ? 'companyActivity.unavailable' : 'companyActivity.empty', {
            category: t(`companyActivity.filters.${filter}`),
          })}
        </Empty>
      ) : null}
    </section>
  );
}

function ActivityRow({ item }: { item: CompanyActivityItem }) {
  const { t } = useTranslation();
  const Icon = ICON[item.kind];
  const tool = item.action ? friendlyTool(item.action) : null;
  const action = item.attempt
    ? `${t('companyActivity.messageAction')} · ${item.action ?? ''}`
    : item.action === 'delegate_task' || item.action === 'spawn_subagent'
      ? `${t('companyActivity.tasks')} · ${item.action}`
      : item.action === 'task_delegated' || item.action === 'task_reported'
        ? t(`companyActivity.${item.action}`)
        : tool
          ? [tool.source, tool.label].filter(Boolean).join(' · ')
          : null;
  return (
    <li
      data-activity-kind={item.kind}
      className={cn(
        'min-w-0 rounded-xl border border-border p-3',
        item.kind === 'system' ? 'border-dashed bg-panel-2' : 'bg-panel',
      )}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <Icon size={15} />
        <span className="font-semibold">{t(`companyActivity.${item.kind}`)}</span>
        {item.attempt ? <span>{t('companyActivity.attempt')}</span> : null}
        <time dateTime={item.createdAt} title={new Date(item.createdAt).toLocaleString()} className="ml-auto">
          {relTime(item.createdAt)}
        </time>
      </div>
      <p className="mt-2 break-words text-sm font-medium">
        {item.from ?? t('companyActivity.unknownAgent')}
        {item.kind !== 'system' || item.to ? <> → {item.to ?? t('companyActivity.unknownTarget')}</> : null}
      </p>
      {action ? <p className="mt-1 break-words text-sm text-fg-2">{action}</p> : null}
      {item.text ? (
        <p className="mt-1 line-clamp-3 whitespace-pre-wrap break-words text-sm text-fg-2">{item.text}</p>
      ) : item.kind !== 'system' ? (
        <p className="mt-1 text-sm text-muted">{t('companyActivity.noPreview')}</p>
      ) : null}
      {item.status ? (
        <p className="mt-1 text-xs text-muted">
          {t('companyActivity.status')}:{' '}
          {t(`companyActivity.state.${item.status}`, { defaultValue: item.status })}
        </p>
      ) : null}
      {item.failedAction ? (
        <p className="mt-1 text-xs text-danger">{t('companyActivity.failedAction')}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-3 text-xs">
        {item.channelId ? (
          <Link
            to="/chat/c/$channelId"
            params={{ channelId: item.channelId }}
            search={{ thread: item.thread }}
            className="text-accent hover:underline"
          >
            {t('companyActivity.openConversation')}
          </Link>
        ) : null}
        {item.runId ? (
          <Link to="/runs/$id" params={{ id: item.runId }} className="text-accent hover:underline">
            {t('companyActivity.openRun')}
          </Link>
        ) : null}
      </div>
      <details className="mt-2 text-xs text-muted">
        <summary className="cursor-pointer rounded py-1 focus-visible:outline-2 focus-visible:outline-accent">
          {t('companyActivity.details')}
        </summary>
        <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded bg-bg p-2">
          {JSON.stringify(item.details, null, 2)}
        </pre>
      </details>
    </li>
  );
}
